import { Component, OnInit } from '@angular/core';
import { ClientProduct, ProductType } from './client-product-mapping-data-model';
import { ClientProductService } from '../../services/client-product.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NoDataTypes, NoDataActionType } from '../../../shared/interfaces/no-data';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';
import {
    ICellClickEventInfo,
    ICustomTableInfoExtend,
} from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { ReportingService } from 'app/modules/reports/services/reporting.service';

@Component({
    selector: 'app-client-product-mapping',
    templateUrl: './client-product-mapping.component.html',
    styleUrls: ['./client-product-mapping.component.scss'],
})
export class ClientProductMappingComponent implements OnInit {
    clientProducts: ClientProduct[];
    productTypes: ProductType[];
    saveBtnDisabled: boolean;
    noDataType: NoDataTypes;
    columnInfoList: ITableColumns[];
    customTableInfo: ICustomTableInfoExtend;
    searchTerm: string;
    action: NoDataActionType;

    constructor(
        private _clientProductService: ClientProductService,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private _toast: ToastService,
        private _reportService: ReportingService
    ) {}

    ngOnInit() {
        this.saveBtnDisabled = true;
        this.searchTerm = '';
        this.action = NoDataActionType.pageStart;
        this.checkForLBAClient();
        this.initiateTableProperties();
        this.getProductData();
    }

    initiateTableProperties() {
        this.columnInfoList = [
            {
                fieldId: 'product',
                fieldName: 'Product Name',
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 350,
                filterable: false,
            },
            {
                fieldId: 'productType',
                fieldName: 'Category',
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'input',
                width: 200,
                filterable: false,
                dataInputProperties: {
                    dataInputType: 'dropdown',
                    key: 'typeKey',
                    valueKey: 'type',
                    isCellDependentDdl: false,
                    widthPercent: 50,
                },
            },
            {
                fieldId: 'productCategory',
                fieldName: 'Type',
                fieldOrder: 2,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                decimalPlaces: 0,
                width: 200,
                filterable: false,
            },
            {
                fieldId: 'entryDate',
                fieldName: 'Entry Date',
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'dateTime',
                decimalPlaces: 0,
                width: 200,
            },
            {
                fieldId: 'lastMod',
                fieldName: 'Modified Date',
                fieldOrder: 4,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'dateTime',
                width: 200,
            },
        ];

        this.customTableInfo = {
            tableId: 'client-product-mapping-table',
            tableHeight: 600,
            hasData: false,
            isDataReady: false,
            columnInfoList: this.columnInfoList,
            dataSet: [],
            searchTerm: this.searchTerm,
            action: this.action,
            title: 'Product Mapping',
            bandColorList: [],
            dataHandleInUiSide: true,
            showSummary: false,
        };
    }

    setNoDataScreen(action: NoDataActionType) {
        this.customTableInfo.dataSet = [];
        this.customTableInfo = this._reportService.refreshCustomMatTable(
            this.customTableInfo,
            false,
            true,
            action,
            this.searchTerm
        );
    }

    getProductData() {
        this.customTableInfo.dataSet = [];
        this.productTypes = [];
        this.clientProducts = [];
        this.customTableInfo = this._reportService.refreshCustomMatTable(
            this.customTableInfo,
            false,
            false,
            this.action,
            this.searchTerm
        );

        this._clientProductService.getClientProducts().subscribe({
            next: (products) => {
                if (products && products.length > 0) {
                    this.clientProducts = products;
                    this._clientProductService.getProductTypes().subscribe({
                        next: (types) => {
                            if (types && types.length > 0) {
                                this.productTypes = types;
                                this.customTableInfo.dataSet = products;
                                this.customTableInfo = this._reportService.refreshCustomMatTable(
                                    this.customTableInfo,
                                    true,
                                    true,
                                    this.action,
                                    this.searchTerm
                                );
                            } else {
                                this.setNoDataScreen(NoDataActionType.pageStart);
                            }
                        },
                        error: (error) => {
                            this.setNoDataScreen(NoDataActionType.error);
                        },
                    });
                } else {
                    this.setNoDataScreen(NoDataActionType.pageStart);
                }
            },
            error: (error) => {
                this.setNoDataScreen(NoDataActionType.error);
            },
        });
    }

    contentChanged(event: ClientProduct[]) {
        this.columnInfoList.forEach((colInf) => {
            if (
                colInf.dataType === 'input' &&
                //colInf.dataInputType === 'dropdown' &&
                colInf.dataInputProperties
            ) {
                event.map((item) => {
                    if (colInf.dataInputProperties.dataInputType === 'dropdown') {
                        if (colInf.dataInputProperties.isCellDependentDdl) {
                            //TODO: Add logic to populate cell dependant ddl
                        } else {
                            item['itemList'] = this.productTypes;
                        }
                    }
                });
            }
        });
    }

    dropdownSelectEvent(event: ICellClickEventInfo) {
        let cellData = event.cellData as ClientProduct;
        let prodType = this.productTypes.find(
            (t) => t.typeKey.toString() === event.cellValue.toString()
        );
        if (prodType) {
            cellData.productType = prodType.type;
            cellData.productCategory = prodType.category;
        }

        this.productTypeChangeHandler(cellData);
    }

    searchRows(event) {
        this.action = NoDataActionType.search;
        this.searchTerm = event;
        this.customTableInfo = this._reportService.refreshCustomMatTable(
            this.customTableInfo,
            true,
            true,
            this.action,
            this.searchTerm
        );
    }

    checkForLBAClient() {
        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        if (clientShortName === 'LBA' && !this.impersonationService.isImpersonatingClient()) {
            return;
        }
    }

    productTypeChangeHandler(changedProduct: ClientProduct) {
        this.clientProducts.find((product) => product.product === changedProduct.product).isDirty =
            true;
        this.saveBtnDisabled = false;
    }

    submitChanges() {
        let changedOrAddedProducts = this.clientProducts
            .filter((product) => product.isDirty)
            .map((product) => {
                return {
                    product: product.product,
                    typeKey: +product.typeKey,
                };
            });
        this._clientProductService.submitChanges(changedOrAddedProducts).subscribe(
            () => {
                this._toast.success('Product Mapping changes saved.');
                this.searchTerm = '';
                this.getProductData();
                this.saveBtnDisabled = true;
                scroll(0, 0);
            },
            (response) => {
                this._toast.error('Error saving changes.');
                console.log(response);
            }
        );
    }

    exportToExcel() {
        this._reportService.setExportColumnsAndExportToExcel(
            this.columnInfoList,
            this.customTableInfo.dataSet,
            'product-mappings_'
        );
    }
}
